import { locateQuoteInArticle } from './article-critique';

/**
 * WHERE A REVIEW NOTE'S PASSAGE IS, in code.
 *
 * The automatic fixer rewrites ONLY the passage a note quotes: the sentence (or the table row) that holds the
 * quote, plus at most one neighbouring sentence. This file finds those bounds in the article's own Markdown, so
 * the model is shown them and the guards can hold its edit to them. Nothing here calls a model.
 *
 * Offsets are into the article's Markdown string, end exclusive.
 */

export interface Span {
  start: number;
  end: number;
}

export type PassageKind = 'sentence' | 'table_row' | 'list_item' | 'heading';

export interface Passage {
  /** The note's quote, resolved to the article's own text (links, emphasis and line breaks read as the reader sees them). */
  quote: Span;
  /** The sentences (or the one table row) that hold the quote. The model's edit lies in these, or one neighbour. */
  core: Span;
  /** The sentence before the core, on the same line. Null for a table row and for the first sentence of a line. */
  previous: Span | null;
  /** The sentence after the core, on the same line. */
  next: Span | null;
  kind: PassageKind;
}

export type PassageResult = { ok: true; passage: Passage } | { ok: false; reason: string };

/** The longest stretch the fixer is asked to rewrite. A longer passage is a person's job. */
export const MAX_PASSAGE_CHARS = 2_000;
/** A quote that runs over more sentences than this is a section, not a passage. */
export const MAX_PASSAGE_SENTENCES = 4;

const ABBREVIATIONS = new Set([
  'sec',
  'secs',
  'no',
  'nos',
  'inc',
  'corp',
  'co',
  'ltd',
  'st',
  'vs',
  'etc',
  'dr',
  'mr',
  'mrs',
  'ms',
  'jr',
  'sr',
  'u.s',
  'e.g',
  'i.e',
  'art',
  'ch',
  'subch',
  'tex',
  'ann',
  'cf',
  'approx',
  'bus',
  'com',
  'comm',
  'util',
  'nat',
  'res',
  'prop',
  'educ',
  'ins',
  'transp',
  'occ',
  'admin',
  'reg',
  'fed',
  'rev',
  'civ',
  'prac',
  'rem',
]);

const LIST_MARKER = /^(\s*(?:[-*+]|\d+[.)])\s+)/;
const QUOTE_MARKER = /^(\s*>\s?)/;
const HEADING = /^\s{0,3}#{1,6}\s/;
const FENCE = /^\s*(?:```|~~~)/;

interface Line {
  start: number;
  end: number;
  text: string;
  kind: PassageKind | 'code' | 'blank';
  /** Characters at the start of the line that are Markdown structure, not prose (list marker, quote mark). */
  prefix: number;
}

function readLines(markdown: string): Line[] {
  const lines: Line[] = [];
  let at = 0;
  let inFence = false;
  for (const text of markdown.split('\n')) {
    const start = at;
    const end = at + text.length;
    at = end + 1;
    if (FENCE.test(text)) {
      inFence = !inFence;
      lines.push({ start, end, text, kind: 'code', prefix: 0 });
      continue;
    }
    if (inFence) {
      lines.push({ start, end, text, kind: 'code', prefix: 0 });
      continue;
    }
    if (!text.trim()) lines.push({ start, end, text, kind: 'blank', prefix: 0 });
    else if (text.trimStart().startsWith('|'))
      lines.push({ start, end, text, kind: 'table_row', prefix: 0 });
    else if (HEADING.test(text)) lines.push({ start, end, text, kind: 'heading', prefix: 0 });
    else {
      const marker = LIST_MARKER.exec(text) ?? QUOTE_MARKER.exec(text);
      lines.push({
        start,
        end,
        text,
        kind: LIST_MARKER.test(text) ? 'list_item' : 'sentence',
        prefix: marker ? marker[1]!.length : 0,
      });
    }
  }
  return lines;
}

/** The line's text with everything a sentence boundary must not be found inside (link targets, code) blanked out. */
function maskForBoundaries(text: string): string {
  const chars = text.split('');
  const blank = (from: number, to: number) => {
    for (let i = from; i < to; i += 1) chars[i] = 'x';
  };
  for (const match of text.matchAll(/\]\(([^)]*)\)/g))
    blank(match.index! + 2, match.index! + match[0].length - 1);
  for (const match of text.matchAll(/`[^`]*`/g))
    blank(match.index!, match.index! + match[0].length);
  for (const match of text.matchAll(/https?:\/\/[^\s)>\]]+/g))
    blank(match.index!, match.index! + match[0].length);
  return chars.join('');
}

function endsWithAbbreviation(masked: string, upTo: number): boolean {
  const head = masked.slice(0, upTo);
  const match = /([A-Za-z][A-Za-z.]*)\.$/.exec(head);
  if (!match) return false;
  const token = match[1]!.toLowerCase();
  return ABBREVIATIONS.has(token) || /^[a-z]$/.test(token);
}

/** The sentences of one line of prose, as offsets into the line. */
function sentencesOfLine(line: Line): Span[] {
  const body = line.text.slice(line.prefix);
  if (!body.trim()) return [];
  const masked = maskForBoundaries(body);
  const spans: Span[] = [];
  let from = 0;
  const boundary = /([.!?]+["”')\]*_]*)(\s+)(?=["“'([*_]*[A-Z0-9$§])/g;
  for (const match of masked.matchAll(boundary)) {
    const terminatorEnd = match.index! + match[1]!.length;
    if (endsWithAbbreviation(masked, terminatorEnd)) continue;
    spans.push({ start: from, end: terminatorEnd });
    from = match.index! + match[0].length;
  }
  spans.push({ start: from, end: body.length });
  const lead = line.prefix;
  const lineStart = line.start;
  return spans
    .map((span) => ({ start: lineStart + lead + span.start, end: lineStart + lead + span.end }))
    .filter((span) => span.end > span.start);
}

/**
 * The passage a quote lies in, or why it cannot be rewritten automatically. A table row is one passage; a
 * sentence is the sentence (or sentences, up to four) the quote touches, with the sentence before and the one
 * after as the only neighbours the fixer may reach into.
 */
export function findPassage(markdown: string, quote: string): PassageResult {
  const located = locateQuoteInArticle(markdown, quote);
  if (!located)
    return { ok: false, reason: 'The quoted passage is not in this version of the article.' };
  const at = markdown.indexOf(located);
  if (at < 0)
    return { ok: false, reason: 'The quoted passage is not in this version of the article.' };
  const quoteSpan: Span = { start: at, end: at + located.length };
  const lines = readLines(markdown);
  const touched = lines.filter(
    (line) => line.kind !== 'blank' && line.end > quoteSpan.start && line.start < quoteSpan.end,
  );
  if (!touched.length)
    return { ok: false, reason: 'The quoted passage is not in this version of the article.' };
  if (touched.some((line) => line.kind === 'code'))
    return {
      ok: false,
      reason: 'The passage is inside a code block, which is not rewritten automatically.',
    };
  const tables = touched.filter((line) => line.kind === 'table_row');
  if (tables.length) {
    if (touched.length > 1)
      return {
        ok: false,
        reason: 'The passage runs over more than one table row, which is a person’s edit.',
      };
    const row = touched[0]!;
    if (row.end - row.start > MAX_PASSAGE_CHARS)
      return { ok: false, reason: 'The table row is too long to rewrite automatically.' };
    return {
      ok: true,
      passage: {
        quote: quoteSpan,
        core: { start: row.start, end: row.end },
        previous: null,
        next: null,
        kind: 'table_row',
      },
    };
  }
  if (touched.length > 2)
    return {
      ok: false,
      reason: 'The passage runs over more than two lines, which is a person’s edit.',
    };
  const heading = touched.find((line) => line.kind === 'heading');
  if (heading) {
    return {
      ok: true,
      passage: {
        quote: quoteSpan,
        core: { start: heading.start, end: heading.end },
        previous: null,
        next: null,
        kind: 'heading',
      },
    };
  }
  const all = touched.flatMap((line) => sentencesOfLine(line));
  const overlapping = all.filter(
    (span) => span.end > quoteSpan.start && span.start < quoteSpan.end,
  );
  if (!overlapping.length)
    return { ok: false, reason: 'No sentence could be found around the quoted passage.' };
  if (overlapping.length > MAX_PASSAGE_SENTENCES)
    return {
      ok: false,
      reason: `The passage runs over ${overlapping.length} sentences, which is a person’s edit.`,
    };
  const core: Span = {
    start: overlapping[0]!.start,
    end: overlapping[overlapping.length - 1]!.end,
  };
  if (core.end - core.start > MAX_PASSAGE_CHARS)
    return { ok: false, reason: 'The passage is too long to rewrite automatically.' };
  const firstIndex = all.indexOf(overlapping[0]!);
  const lastIndex = all.indexOf(overlapping[overlapping.length - 1]!);
  const previous = firstIndex > 0 ? all[firstIndex - 1]! : null;
  const next = lastIndex < all.length - 1 ? all[lastIndex + 1]! : null;
  const firstLine = touched[0]!;
  return {
    ok: true,
    passage: {
      quote: quoteSpan,
      core,
      previous,
      next,
      kind: firstLine.kind === 'list_item' ? 'list_item' : 'sentence',
    },
  };
}

/** The stretches an edit may lie wholly inside: the core, the core with its previous sentence, the core with its next. */
export function allowedWindows(passage: Passage): Span[] {
  const windows: Span[] = [passage.core];
  if (passage.previous) windows.push({ start: passage.previous.start, end: passage.core.end });
  if (passage.next) windows.push({ start: passage.core.start, end: passage.next.end });
  return windows;
}

export const slice = (markdown: string, span: Span): string => markdown.slice(span.start, span.end);

/** How many times `needle` occurs in `haystack` (non-overlapping), so an edit can be refused when it is ambiguous. */
export function occurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) return count;
    count += 1;
    from = at + needle.length;
  }
}

/** True when `outer` holds `inner` entirely. */
export const holds = (outer: Span, inner: Span): boolean =>
  inner.start >= outer.start && inner.end <= outer.end;

/** True when two spans share at least one character. */
export const overlaps = (a: Span, b: Span): boolean => a.start < b.end && b.start < a.end;

/** A passage is one sentence when it splits into one. Used to route a wrong-law claim to the passage fixer. */
export function isSingleSentence(text: string): boolean {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return false;
  if (flat.includes('|') && flat.split('|').filter((cell) => cell.trim()).length > 1) return true;
  const sentences = sentencesOfLine({
    start: 0,
    end: flat.length,
    text: flat,
    kind: 'sentence',
    prefix: 0,
  });
  return sentences.length === 1;
}
